import {
	IModel,
	db,
	IIdentifiable,
	enumOrDefault,
	VIPCompany,
	IVIPCompany,
	IVIPCompanyOptions,
	VIPUser,
	IVIPUser,
	IVIPUserOptions,
	VIPContact,
	IVIPContact,
	IVIPContactOptions,
	VIPCertificate,
	IVIPCertificate,
	IVIPCertificateOptions,
	VIPProjectIp,
	IVIPProjectIp,
	IVIPProjectIpOptions,
} from "@/loader";

export enum VipProjectStatus {
	bid = 0,
	signed = 1,
	cancelled = 2,
	ended = 3,
	archived = 4,
}

export function vipProjectStatusOrDefault(value: any, defaultValue: VipProjectStatus = VipProjectStatus.bid): VipProjectStatus {
	return <VipProjectStatus>enumOrDefault(value, defaultValue, Object.values(VipProjectStatus));
}

export function getVipProjectStatusLabel(value: VipProjectStatus): string {
	switch (value) {
		case VipProjectStatus.bid:
			return window.wp.i18n._x("Bid", "Project", "vip-vue");
		case VipProjectStatus.signed:
			return window.wp.i18n._x("Signed", "Project", "vip-vue");
		case VipProjectStatus.cancelled:
			return window.wp.i18n._x("Cancelled", "Project", "vip-vue");
		case VipProjectStatus.ended:
			return window.wp.i18n._x("Ended", "Project", "vip-vue");
		case VipProjectStatus.archived:
			return window.wp.i18n._x("Archived", "Project", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Status", "Project", "vip-vue");
	}
}

export function getVipProjectStatusList(): { value: any, text: string }[] {
	return [
		{ value: 0, text: window.wp.i18n._x("Bid", "Project", "vip-vue") },
		{ value: 1, text: window.wp.i18n._x("Signed", "Project", "vip-vue") },
		{ value: 2, text: window.wp.i18n._x("Cancelled", "Project", "vip-vue") },
		{ value: 3, text: window.wp.i18n._x("Ended", "Project", "vip-vue") },
		{ value: 4, text: window.wp.i18n._x("Archived", "Project", "vip-vue") },
	];
}

export type VIPProjectFields = "id" | "fk_company" | "fk_manager" | "fk_contact" | "status" | "ref" | "name" | "description";

/**
 * Project Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPProjectBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Company ID */
	fk_company?: number;
	/** Manager ID */
	fk_manager?: number;
	/** Contact ID */
	fk_contact?: number | null;
	/** Status */
	status?: VipProjectStatus;
	/** Name */
	name?: string;
	/** Description */
	description?: string | null;
	/** Ref */
	ref?: string | null;

	/** Company */
	company?: IVIPCompany | null;
	/** Manager */
	manager?: IVIPUser | null;
	/** Contact */
	contact?: IVIPContact | null;

	/** Certificates */
	certificates?: IVIPCertificate[];
	/** Project ip s */
	ips?: IVIPProjectIp[];
}

/**
 * Project Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPProjectOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Company ID should be included or not. Default true. */
	fk_company?: boolean;
	/** Indicates whether the Manager ID should be included or not. Default true. */
	fk_manager?: boolean;
	/** Indicates whether the Contact ID should be included or not. Default true. */
	fk_contact?: boolean;
	/** Indicates whether the Status should be included or not. Default true. */
	status?: boolean;
	/** Indicates whether the Name should be included or not. Default true. */
	name?: boolean;
	/** Indicates whether the Description should be included or not. Default true. */
	description?: boolean;
	/** Indicates whether the Ref should be included or not. Default true. */
	ref?: boolean;
	/** Indicates whether the Company should be included or not. Default false. */
	company?: boolean | IVIPCompanyOptions;
	/** Indicates whether the Manager should be included or not. Default false. */
	manager?: boolean | IVIPUserOptions;
	/** Indicates whether the Contact should be included or not. Default false. */
	contact?: boolean | IVIPContactOptions;
	/** Indicates whether the Certificates should be included or not. Default false. */
	certificates?: boolean | IVIPCertificateOptions;
	/** Indicates whether the Project ip s should be included or not. Default false. */
	ips?: boolean | IVIPProjectIpOptions;
}

/**
 * Project Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPProjectBase implements IVIPProjectBase, IModel<IVIPProjectBase> {
	/** Id */
	public id!: number;
	/** Company ID */
	public fk_company!: number;
	/** Manager ID */
	public fk_manager!: number;
	/** Contact ID */
	public fk_contact!: number | null;
	/** Status */
	public status!: VipProjectStatus;
	/** Name */
	public name!: string;
	/** Description */
	public description!: string | null;
	/** Ref */
	public ref!: string | null;

	/**
	 * Get Company
	 */
	public get company(): VIPCompany | null {
		return db.companies.get(this.fk_company);
	}

	/**
	 * Set Company
	 */
	public set company(value: VIPCompany | null) {
		this.fk_company = Number.orDefault(value?.id);
	}

	/**
	 * Get Manager
	 */
	public get manager(): VIPUser | null {
		return db.users.get(this.fk_manager);
	}

	/**
	 * Set Manager
	 */
	public set manager(value: VIPUser | null) {
		this.fk_manager = Number.orDefault(value?.id);
	}

	/**
	 * Get Contact
	 */
	public get contact(): VIPContact | null {
		return this.fk_contact && db.contacts.get(this.fk_contact) || null;
	}

	/**
	 * Set Contact
	 */
	public set contact(value: VIPContact | null) {
		this.fk_contact = Number.nullable(value?.id);
	}

	/**
	 * Get Certificates
	 */
	public get certificates(): VIPCertificate[] {
		return db.certificates.list.filter(x => x.fk_project === this.id);
	}

	/**
	 * Get Project ip s
	 */
	public get ips(): VIPProjectIp[] {
		return db.projectIps.list.filter(x => x.fk_project === this.id);
	}

	/**
	 * Check if this Status is Bid.
	 *
	 * @return bool
	 */
	public get isStatusBid(): boolean {
		return this.status === VipProjectStatus.bid;
	}

	/**
	 * Check if this Status is Signed.
	 *
	 * @return bool
	 */
	public get isStatusSigned(): boolean {
		return this.status === VipProjectStatus.signed;
	}

	/**
	 * Check if this Status is Cancelled.
	 *
	 * @return bool
	 */
	public get isStatusCancelled(): boolean {
		return this.status === VipProjectStatus.cancelled;
	}

	/**
	 * Check if this Status is Ended.
	 *
	 * @return bool
	 */
	public get isStatusEnded(): boolean {
		return this.status === VipProjectStatus.ended;
	}

	/**
	 * Check if this Status is Archived.
	 *
	 * @return bool
	 */
	public get isStatusArchived(): boolean {
		return this.status === VipProjectStatus.archived;
	}

	/**
	 * Get the Status label
	 *
	 * @return string
	 */
	public getStatusLabel(): string {
		return getVipProjectStatusLabel(this.status);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPProjectBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPProjectBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPProjectBase) {
		this.id = Number.orDefault(data.id);
		this.fk_company = Number.orDefault(data.fk_company);
		this.fk_manager = Number.orDefault(data.fk_manager);
		this.fk_contact = Number.nullable(data.fk_contact);
		this.status = vipProjectStatusOrDefault(data.status);
		this.name = String.orDefault(data.name);
		this.description = String.nullable(data.description);
		this.ref = String.nullable(data.ref);
		
		if (data.company) {
			db.companies.insertOrUpdate(data.company);
		}
		if (data.manager) {
			db.users.insertOrUpdate(data.manager);
		}
		if (data.contact) {
			db.contacts.insertOrUpdate(data.contact);
		}
		
		if (Array.isArray(data.certificates) && data.certificates.length) {
			db.certificates.insertOrUpdateMany(data.certificates);
		}
		if (Array.isArray(data.ips) && data.ips.length) {
			db.projectIps.insertOrUpdateMany(data.ips);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPProjectBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPProjectBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_company !== undefined) {
			this.fk_company = Number.orDefault(data.fk_company);
		}
		if (data.fk_manager !== undefined) {
			this.fk_manager = Number.orDefault(data.fk_manager);
		}
		if (data.fk_contact !== undefined) {
			this.fk_contact = Number.nullable(data.fk_contact);
		}
		if (data.status !== undefined) {
			this.status = vipProjectStatusOrDefault(data.status);
		}
		if (data.name !== undefined) {
			this.name = String.orDefault(data.name);
		}
		if (data.description !== undefined) {
			this.description = String.nullable(data.description);
		}
		if (data.ref !== undefined) {
			this.ref = String.nullable(data.ref);
		}
		
		if (data.company) {
			db.companies.insertOrUpdate(data.company);
		}
		if (data.manager) {
			db.users.insertOrUpdate(data.manager);
		}
		if (data.contact) {
			db.contacts.insertOrUpdate(data.contact);
		}
		
		if (Array.isArray(data.certificates) && data.certificates.length) {
			db.certificates.insertOrUpdateMany(data.certificates);
		}
		if (Array.isArray(data.ips) && data.ips.length) {
			db.projectIps.insertOrUpdateMany(data.ips);
		}
	}
}
